import React from "react";

/**
 * Generisches Auffangnetz gegen unbeabsichtigte Mehrfach-Ausführung durch schnelle
 * Doppelklicks/Doppel-Taps. Solange ein Lauf der gewickelten Aktion noch aktiv ist,
 * werden weitere Aufrufe synchron verworfen.
 *
 * Anders als ein `useState`-Flag (`loading`) greift die Sperre sofort beim zweiten
 * Klick — ein `useState` würde erst nach dem nächsten Render aktualisiert, sodass zwei
 * unmittelbar aufeinanderfolgende Klicks beide durchlaufen. Der Ref-basierte Riegel
 * schließt diese Lücke.
 *
 * Einsatz: Click-Handler, die genau einmal pro Vorgang laufen dürfen (Anlegen,
 * Speichern, Absenden), in den Wrapper geben statt direkt zu binden.
 */
export function useGuardedAction<TArgs extends unknown[]>(
  action: (...args: TArgs) => Promise<void> | void
): (...args: TArgs) => Promise<void> {
  const runningRef = React.useRef(false);
  const actionRef = React.useRef(action);
  actionRef.current = action;

  return React.useCallback(async (...args: TArgs) => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      await actionRef.current(...args);
    } finally {
      runningRef.current = false;
    }
  }, []);
}
